import { useLayoutEffect, useRef, useState } from 'react';

export type DropdownPosition = { top: number; left: number; width: number };

// Dropdown menus are portaled to document.body and positioned as `fixed`
// so they aren't clipped by a scrollable/overflow-hidden ancestor (e.g. a
// horizontally-scrolling table). This tracks the trigger's on-screen
// position and keeps the menu glued to it while open.
//
// Position is intentionally left in place (not reset to null) when closing,
// so a caller animating the close transition has a stable position to
// animate from instead of the menu jumping/disappearing mid-transition.
export default function useDropdownPosition<T extends HTMLElement>(isOpen: boolean) {
    const triggerRef = useRef<T>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<DropdownPosition | null>(null);

    useLayoutEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const updatePosition = () => {
            if (triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
            }
        };
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    return { triggerRef, menuRef, position };
}
